import { Head } from 'vite-react-ssg';
import { SERVICES } from '../content/services';

export const SITE = {
  origin: 'https://www.revivebuilders.org',
  name: 'Revive Builders LLC',
  phone: '+1-210-393-1759',
  phoneDisplay: '(210) 393-1759',
  email: 'mrocha@revivebuildersllc.com',
  areaServed: ['San Antonio', 'Austin', 'New Braunfels', 'San Marcos', 'Central Texas'],
  ogImage: '/images/aerial.jpg',
};

/** Org-level JSON-LD, included once per page. */
export function orgJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'GeneralContractor',
    '@id': `${SITE.origin}/#org`,
    name: SITE.name,
    url: `${SITE.origin}/`,
    telephone: SITE.phone,
    email: SITE.email,
    foundingDate: '2021',
    image: `${SITE.origin}/images/aerial.jpg`,
    logo: `${SITE.origin}/logo.svg`,
    description:
      'General contractor for multi-family and commercial properties across Central Texas — apartment unit turns, make-ready services, full-property renovation, roofing, and commercial build-outs.',
    address: {
      '@type': 'PostalAddress',
      addressRegion: 'TX',
      addressLocality: 'San Antonio',
      addressCountry: 'US',
    },
    areaServed: SITE.areaServed.map((name) => ({ '@type': 'Place', name })),
    priceRange: '$$',
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Construction & Property Services',
      itemListElement: SERVICES.map((s) => ({
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: s.name,
          url: `${SITE.origin}/services/${s.slug}`,
        },
      })),
    },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '08:00',
      closes: '18:00',
    },
    sameAs: ['https://g.page/r/CTJUT9-6Wx6GEAE'],
  };
}

interface SeoProps {
  title: string;
  description: string;
  /** Path beginning with '/', e.g. '/services/apartment-make-ready' */
  path: string;
  ogImage?: string;
  /** Extra JSON-LD blocks (Service, FAQPage, BreadcrumbList, …) */
  jsonLd?: object[];
}

export function Seo({ title, description, path, ogImage, jsonLd = [] }: SeoProps) {
  const url = `${SITE.origin}${path === '/' ? '/' : path}`;
  const img = `${SITE.origin}${ogImage ?? SITE.ogImage}`;
  const blocks = [orgJsonLd(), ...jsonLd];
  return (
    <Head>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta name="twitter:card" content="summary_large_image" />
      {blocks.map((b, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(b)}
        </script>
      ))}
    </Head>
  );
}

export function breadcrumbJsonLd(items: { name: string; path: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: `${SITE.origin}${it.path}`,
    })),
  };
}
